import React, {useEffect, useRef, useState} from 'react';
import {Link, useParams} from "react-router-dom";
import {HeaderSchema} from "./Header.schema";
import {GeneralMethods} from "@/views/Admin/Events/Event/General/General.methods";
import {HeaderPropsInterface} from "@/views/Admin/Events/Event/General/Header/Header.props.interface";

import {Button, Modal, Select, useToast} from "uikit";
import styles from './Header.module.scss';

import ChevronLeft from '@/assets/images/icons/12/chevron-left.svg';
import RocketIcon from '@/assets/images/icons/16/new-rocket.svg';
import TickerIcon from '@/assets/images/icons/16/new-ticket.svg';
import UploadIcon from '@/assets/images/icons/16/import.svg';
import DotsIcon from '@/assets/images/icons/16/dots-new.svg';
import EyeIcon from '@/assets/images/icons/16/eye_1.svg';

import ReCAPTCHA from "react-google-recaptcha/lib/esm";
import {useTranslation} from "react-i18next";
import {GeneralInterface} from "@/views/Admin/Events/Event/General/General.interface";
import CrossIcon from "@/assets/images/icons/12/close.svg";

const Header = ({data, onChange, onUpdate}: HeaderPropsInterface) => {
    const [{
        isMenu,
        releasePeriod,
        recaptchaKey,
        isModalOpen,
        permissions,
        eventType,
        checkUrl,
        cabinetUrl
    }, setData] = useState<GeneralInterface>({...HeaderSchema, ...data});
    const { publishEvent } = GeneralMethods;
    const params: { id: any } = useParams();
    const {t} = useTranslation('common');
    const addToast = useToast();

    const permissionsType = eventType === 'courses' ? permissions?.courses : permissions?.events;

    const mobileBtnRef = useRef<HTMLDivElement | null>(null);
    const gResponseRef = useRef(null);

    useEffect(() => {
        if (recaptchaKey) handleRecaptcha()
    }, [recaptchaKey]);

    const handleRecaptcha = async () => {
        gResponseRef.current = await gResponseRef.current.executeAsync()
        await handlePublishEvent(releasePeriod ? releasePeriod : 'P0D');
    };

    const handlePublishEvent = async (releasePeriod: string) => {
        let data = {
            release_period: String(releasePeriod)
        }
        try {
            if (gResponseRef.current) {
                data['recaptcha'] = gResponseRef.current
            }
            const response = await publishEvent(eventType, params?.id, data);
            if ([200, 201].includes(response.status)) {
                setData(prev => ({...prev, isModalOpen: !isModalOpen}))
                await onUpdate()
                addToast({
                    message: `Событие опубликовано`,
                    type: 'success',
                    duration: 3000,
                });
            }
        } catch ({response}) {
            if ([403, 422, 404, 500].includes(response.status) && !response.data.errors.recaptcha) {
                addToast({
                    message: response.data.message,
                    type: 'danger',
                    duration: 3000,
                });
            }
            if (response.data.errors.recaptcha) {
                setData(prev => ({
                    ...prev,
                    recaptchaKey: response.data.errors.recaptcha[0],
                }));
            }
        }
    }

    const handleButtonClick = (event: React.MouseEvent<HTMLDivElement>) => {
        let btn, target

        btn = mobileBtnRef.current;
        target = event.target as HTMLElement;

        setData(prev => ({...prev, isMenu: btn.contains(target) ? !prev.isMenu : prev.isMenu}))
    };

    const handleButtonBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        const relatedTarget = event.relatedTarget;
        const isMobileButton = relatedTarget === mobileBtnRef.current?.children[0];

        if (isMobileButton) return;

        setData(prev => ({...prev, isMenu: false}));
    }

    return (
        <div className={styles.container}>
            {eventType === 'events'
                ? <a href={'/admin/events/'} className={styles.link}>
                    <ChevronLeft/>
                    {t('events')}
                </a>
                : <a href={'/admin/courses/'} className={styles.link}>
                    <ChevronLeft/>
                    {t('course')}
                </a>
            }
            <div
                className={styles.controls}
                onClick={handleButtonClick}
                onBlur={handleButtonBlur}>
                <div className={styles.mobileBtn} ref={mobileBtnRef}>
                    <Button
                        size={'small'}
                        theme={'outline'}
                        type={'secondary'}>
                        <DotsIcon/>
                    </Button>
                </div>
                <div className={[styles.controlBtns, isMenu ? styles.isOpen : null].filter(x => x ?? x).join(' ')}>
                    {eventType === 'courses' ?
                        <a href={cabinetUrl} target={'_blank'}>
                            <Button
                                size={'small'}
                                theme={'outline'}
                                type={'secondary'}>
                                <EyeIcon/>
                                {t('watch_course')}
                            </Button>
                        </a> : null
                    }
                    <a href={"/admin/promotion/"}>
                        <Button
                            size={'small'}
                            theme={'outline'}
                            type={'secondary'}>
                            <RocketIcon/>
                            {t('promotion')}
                        </Button>
                    </a>
                    {eventType === 'events' ?
                        <a href={`${checkUrl}`}>
                            <Button
                                size={'small'}
                                theme={'outline'}
                                type={'secondary'}>
                                <TickerIcon/>
                                {t('control')}
                            </Button>
                        </a> : null
                    }
                </div>
                {data.fields.statusCode.value === 0 && permissionsType['do-publish'] ?
                    <div className={styles.public}>
                        <Link to={"#"}>
                            <Button
                                size={'small'}
                                theme={'default'}
                                type={'primary'}
                                onClick={() => setData(prev => ({...prev, isModalOpen: true}))}>
                                <UploadIcon/>
                                <span>{t('publish')}</span>
                            </Button>
                        </Link>
                    </div> : null
                }
                <Modal
                    style={{padding: '16px', maxWidth: '720px', width: '100%', position: 'relative'}}
                    className={'anyCustomClass'}
                    active={isModalOpen}
                    onActive={(e: boolean) => setData(prev => ({...prev, isModalOpen: e}))}
                    onMissClick={(event) => console.log(event)}
                    onEscape={(event) => console.log(event)}
                >
                    <div className={styles.modal}>
                        <h6 className={styles.modalTitle}>Публикация события</h6>
                        <div className={styles.modalSubtitle}>
                            {t('release_period')}.
                        </div>
                        <div
                            className={styles.modalCross}
                            onClick={() => setData(prev => ({...prev, isModalOpen: false}))}
                        >
                            <CrossIcon/>
                        </div>
                        {(data.constants?.release_period && data.fields.releasePeriodLabel.value) ?
                            <Select
                                data={data.constants?.release_period}
                                value={releasePeriod ? `${releasePeriod}` : 'P0D'}
                                onChange={(e) => setData(prev => ({...prev, releasePeriod: e}))}
                                label={data.fields.releasePeriodLabel.value}
                                direction={['bottom', 'center']}/> : null
                        }
                        <div className={styles.modalControl}>
                            <Button
                                size={'small'}
                                theme={'ghost'}
                                type={'tertiary'}
                                onClick={() => setData(prev => ({...prev, isModalOpen: false}))}>
                                <span>{t('cancel')}</span>
                            </Button>
                            {recaptchaKey ? <ReCAPTCHA
                                sitekey={String(recaptchaKey)}
                                size='invisible'
                                ref={gResponseRef}
                            /> : null}
                            <Button
                                size={'small'}
                                theme={'default'}
                                type={'primary'}
                                onClick={() => handlePublishEvent(releasePeriod ? String(releasePeriod) : 'P0D')}>
                                <span>{t('continue')}</span>
                            </Button>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default Header;
